import { useCallback, useEffect, useRef } from "react";
import { useMap, useMapEvents } from "react-leaflet";
import Geohash from "latlon-geohash";
import GeohashService from "../services/geohash.service";

const BUFFER_FACTOR = 0.12;
const DEBOUNCE_MS = 350;

function getBufferedBBox(map) {
  const sw = map.getBounds().getSouthWest();
  const ne = map.getBounds().getNorthEast();
  const latRange = ne.lat - sw.lat;
  const lngRange = ne.lng - sw.lng;
  return {
    south: Math.max(sw.lat - latRange * BUFFER_FACTOR, -90),
    north: Math.min(ne.lat + latRange * BUFFER_FACTOR, 90),
    west: Math.max(sw.lng - lngRange * BUFFER_FACTOR, -180),
    east: Math.min(ne.lng + lngRange * BUFFER_FACTOR, 180),
  };
}

function bboxContains(outer, inner) {
  if (!outer) return false;
  return (
    outer.south <= inner.south &&
    outer.north >= inner.north &&
    outer.west <= inner.west &&
    outer.east >= inner.east
  );
}

function cornerSig(bbox) {
  return (
    Geohash.encode(bbox.south, bbox.west, 2) +
    Geohash.encode(bbox.south, bbox.east, 2) +
    Geohash.encode(bbox.north, bbox.west, 2) +
    Geohash.encode(bbox.north, bbox.east, 2)
  );
}

/**
 * Viewport'u backend'e bbox olarak gönderir.
 * İki katmanlı guard:
 *   1) Önceki istekteki bbox mevcut viewport'u hâlâ kapsıyorsa → istek yok
 *   2) 4 köşenin 2'li geohash birleşimi öncekiyle aynıysa → istek yok
 */
export default function WorldExploreBoundsFetcher({ onData }) {
  const map = useMap();
  const debounceRef = useRef(null);
  const lastSentBBoxRef = useRef(null);
  const lastCornerSigRef = useRef("");

  const fetchForBounds = useCallback(() => {
    const current = getBufferedBBox(map);

    if (bboxContains(lastSentBBoxRef.current, current)) {
      return;
    }

    const sig = cornerSig(current);
    if (sig === lastCornerSigRef.current) {
      return;
    }

    lastSentBBoxRef.current = current;
    lastCornerSigRef.current = sig;

    GeohashService.getExploreData(current)
      .then((res) => {
        if (res?.data && typeof res.data === "object") {
          onData(res.data);
        }
      })
      .catch(() => {
        lastSentBBoxRef.current = null;
        lastCornerSigRef.current = "";
      });
  }, [map, onData]);

  const debouncedFetch = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(fetchForBounds, DEBOUNCE_MS);
  }, [fetchForBounds]);

  useMapEvents({
    moveend: debouncedFetch,
    zoomend: debouncedFetch,
    resize: debouncedFetch,
  });

  useEffect(() => {
    fetchForBounds();
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [fetchForBounds]);

  return null;
}
